import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Card, IconButton, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import Person2Icon from '@mui/icons-material/Person2';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useEffect, useState } from "react";
import { ButtonComponent, FormProviderComponent, TextFieldComponent } from '../../components/form';
import DialogComponent from '../../components/dialog/dialogComponent';
import { useEditProfileDetailsMutation } from '../../services/profileApi';
import SnackbarComponent from '../../components/snackBar/snackbarComponent';

const rowDetails = (icon, label, value, isLoading=false) => {
    return <Stack
        direction='row'
        justifyContent='start'
        alignItems='start'
        width='100%'
    >
        {icon}

        <Stack
            direction='column'
            justifyContent='center'
            alignItems='start'
            pl={{xs: 4, md: 4, lg: 9}}
            width='100%'
        >  
            <Typography
                fontWeight={400}
                fontSize={{xs: 14, md: 16, lg: 22}}
                letterSpacing={0.3}
                color="#121212"
            >
                {label}
            </Typography>

            {
                isLoading
                ?
                <Skeleton variant="rounded" width='100%' height={50} />
                :
                <Typography
                    fontWeight={400}
                    fontSize={{xs: 14, md: 16, lg: 22}}
                    letterSpacing={0.3}
                    color="#5E5959"
                    sx={{
                        wordWrap: 'break-word'
                    }}
                >
                    {value}
                </Typography> 
            }
        </Stack>
    </Stack>
}

const ProfileDetails = ({
    data,
    isLoading,
    loadLatestProfileDetails
}) => {
    const theme = useTheme();

    const [editMode, setEditMode] = useState(false);

    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const [openEditOption, setOpenEditOption] = useState(false);

    const enableEdit = useCallback(() => {
        if(isMdUp){
            setEditMode(true);
        }
        else{
            setOpenEditOption(true);
        }
    }, []);

    const getUserName = () => {
        const firstName = data?.firstName;
        const lastName = data?.lastName;

        let name = "";

        if(firstName && firstName.trim().length > 0) name += firstName + " ";
        if(lastName && lastName.trim().length > 0) name += lastName;

        return name;
    }

    return (
        <>
            <Stack
                direction='column'
                justifyContent='center'
                alignItems='center'
                width='100%'
            >
                <Card
                    sx={{
                        width: '100%'
                    }}
                >
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                        spacing={{xs: 2, md: 3, lg: 4}}
                        py={{xs: 2, md: 4, lg: 6}}
                        px={{xs: 2.5, md: 4, lg: 10}}
                    >
                        {
                            editMode
                            ?
                            <Stack
                                direction='row'
                                justifyContent='start'
                                alignItems='start'
                                width='100%'
                            >
                                <Person2Icon 
                                    sx={{
                                        width: {xs: 22, md: 32, lg: 40},
                                        height: {xs: 22, md: 32, lg: 40},
                                        color: {xs: 'black', md: theme.palette.primary.main}
                                    }}
                                />

                                <Stack
                                    direction='column'
                                    justifyContent='start'
                                    alignItems='start'
                                    width='100%'
                                    pl={{xs: 4, md: 4, lg: 9}}
                                >
                                    <ProfileEditForm 
                                        data={data}
                                        setEditMode={setEditMode}
                                        loadLatestProfileDetails={loadLatestProfileDetails}
                                    />
                                </Stack>
                            </Stack>
                            :
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='start'
                                width='100%'
                            >
                                {
                                    rowDetails(
                                        <Person2Icon 
                                            sx={{
                                                width: {xs: 22, md: 32, lg: 40},
                                                height: {xs: 22, md: 32, lg: 40},
                                                color: {xs: 'black', md: theme.palette.primary.main}
                                            }}
                                        />,
                                        'Name',
                                        getUserName(),
                                        isLoading
                                    )
                                }

                                {
                                    isLoading
                                    ?
                                    <></>
                                    :
                                    <IconButton 
                                        sx={{
                                            justifyContent: 'end'
                                        }}
                                        onClick={enableEdit}
                                    >
                                        <EditIcon sx={{color: '#5E5959', width: 22.5, height: 22.5}}/>
                                    </IconButton>
                                }
                            </Stack>
                        }

                        {
                            rowDetails(
                                <EmailIcon 
                                    sx={{
                                        width: {xs: 22, md: 32, lg: 40},
                                        height: {xs: 22, md: 32, lg: 40},
                                        color: {xs: 'black', md: theme.palette.primary.main}
                                    }}
                                />,
                                'Email',
                                data?.email,
                                isLoading
                            )
                        }

                        {
                            rowDetails(
                                <LocalPhoneIcon 
                                    sx={{
                                        width: {xs: 22, md: 32, lg: 40},
                                        height: {xs: 22, md: 32, lg: 40},
                                        color: {xs: 'black', md: theme.palette.primary.main}
                                    }}
                                />,
                                'Contact Number',
                                data?.phoneNo ?? data?.contactNo,
                                isLoading
                            )
                        }

                        {
                            rowDetails(
                                <EngineeringIcon 
                                    sx={{
                                        width: {xs: 22, md: 32, lg: 40},
                                        height: {xs: 22, md: 32, lg: 40},
                                        color: {xs: 'black', md: theme.palette.primary.main}
                                    }}
                                />,
                                'Role',
                                Array.isArray(data?.roles) ? data.roles[0] : data?.role,
                                isLoading
                            )
                        }
                    </Stack>
                </Card>
            </Stack>

            {/* Mobile view profile edit */}
            <DialogComponent 
                open={openEditOption}
                title="Profile Edit"
                body={<>
                    <ProfileEditForm 
                        data={data}
                        setEditMode={setEditMode}
                        loadLatestProfileDetails={loadLatestProfileDetails}
                    />
                </>}
                hideAction
                handleClose={() => setOpenEditOption(false)}
                closeCallback={() => setOpenEditOption(false)}
                fullScreen
            />
        </>
    );
}

export default ProfileDetails;

const ProfileEditForm = ({
    data,
    setEditMode,
    loadLatestProfileDetails
}) => {
    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const formSchema = yup.object().shape({
        fname: yup.string().required('First name is required'),
        lname: yup.string().required('Last name is required'),
    });
        
    const defaultValues = {
        fname: '',
        lname: ''
    };
        
    const formMethods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: defaultValues
    });
        
    const {
        setValue,
        reset: reset,
        handleSubmit: handleSubmit,
        formState: { isSubmitting: isSubmitting },
        clearErrors,
        watch
    } = formMethods;
        
    const values = watch();

    const [
        editProfileData, 
        {
            isLoading: isLoadingEditProfileData,
            isSuccess: isSuccessEditProfileData,
            isError: isErrorEditProfileData,
            error: errorEditProfileData
        }
    ] = useEditProfileDetailsMutation();
    
    useEffect(() => {
        if(isSuccessEditProfileData){
            setEditMode(false);

            setSnackBar({
                message: "Profile successfully updated!",
                color: "green",
                open: true
            });

            loadLatestProfileDetails();
        }
        else if(isErrorEditProfileData && errorEditProfileData){
            setSnackBar({
                message: errorEditProfileData?.data?.message ?? "Something went wrong!",
                color: "red",
                open: true
            });
        }
    }, [isSuccessEditProfileData, isErrorEditProfileData, errorEditProfileData]);

    const onSubmit = handleSubmit(async(formData) => {
        console.log(formData);

        const body = {
            "phoneNo": data.phoneNo ?? data.contactNo,
            "email": data.email,
            "firstName": formData.fname,
            "lastName": formData.lname,
            "profileImg": data.profileImg ?? data.profileImage,
            "role": Array.isArray(data?.roles) ? data.roles[0] : data?.role,
            "staffId": data.staffId
        }

        console.log(body);

        editProfileData({body: body});
    });

    useEffect(() => {
        if(data){
            setValue('fname', data.firstName);
            setValue('lname', data.lastName);
        }

        clearErrors();
    }, [data]);

    return (
        <>
            <FormProviderComponent methods={formMethods} onSubmit={onSubmit}>
                <Stack
                    direction={{xs: 'column', md: 'row'}}
                    justifyContent={{xs: 'start', md: 'space-between'}}
                    alignItems={{xs: 'center', md: 'end'}}
                    width='100%'
                    spacing={{xs: 0, md: 2, lg: 5}}
                >
                    <Box width='100%'>
                        <TextFieldComponent 
                            name='fname'
                            label='First Name'
                            fullWidth
                        />
                    </Box>

                    <Box width='100%'>
                        <TextFieldComponent 
                            name='lname'
                            label='Last Name'
                            fullWidth
                        />
                    </Box>
                </Stack>

                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='start'
                    width='100%'
                    spacing={{xs: 0, md: 1.5, lg: 2}}
                    pt={1}
                >
                    <ButtonComponent 
                        label="Cancel"
                        onClick={() => setEditMode(false)}
                        fullWidth
                        sx={{
                            display: {xs: 'none', md: 'block'}
                        }}
                        disabled={isLoadingEditProfileData}
                    />

                    <ButtonComponent 
                        label="Update"
                        type="submit"
                        fullWidth
                        inLoading={isLoadingEditProfileData}
                    />
                </Stack>
            </FormProviderComponent>

            <SnackbarComponent
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />
        </>
    );
}